import React, { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import './MessageCenterFaxStyle.css'
import FaxCompose from './Compose/MessageCenterFaxCompose.component'
import FaxPreview from './Preview/MessageCenterFaxPreview.component'
import useFax from './useFax'
import { Button, InputField, SearchWithFilter } from '@components/common'
import { EmptyState } from '@components/ui'
import { FaxTagColor } from '@dataTypes/fax.type'
import { useMessageCenterTabLayer } from '../MessageCenterTabLayer.context'
import { DropDownButtonComponent } from '@syncfusion/ej2-react-splitbuttons'
import { useAppSelector } from '@store/hooks'
import MessageCenterDeleteFaxOrganizeModal from '../MessageCenterDeleteFaxOrganizeModal.component'

function MessageCenterFax(): React.ReactElement {
  const faxHooks = useFax()
  const location = useLocation()
  const { setTabLayerActions } = useMessageCenterTabLayer()
  const faxes = useAppSelector((state) => state.fax.data) ?? []
  const faxLoading = useAppSelector((state) => state.fax.loading)
  const showFullEmptyState =
    !faxLoading && faxes.length === 0 && faxHooks.activeTab !== 'compose'

  useEffect(() => {
    const routeState = location.state as {
      faxId?: string
      faxFolder?: 'inbox' | 'sent'
    } | null
    if (!routeState?.faxId) return
    const targetFolder = routeState.faxFolder ?? 'inbox'
    if (faxHooks.activeTab === 'compose') {
      faxHooks.handleChangeTab(targetFolder)
    }
  }, [location.state, faxHooks.activeTab, faxHooks.handleChangeTab])

  useEffect(() => {
    if (faxHooks.activeFolder !== 'compose')
      setTabLayerActions(
        <div className="mc-contact-buttons">
          <SearchWithFilter
            searchIcon="mgc_search_2_line"
            filterIcon="mgc_settings_2_line"
            placeholder="Search"
            searchQuery={faxHooks.searchQuery}
            onChange={faxHooks.handleSearch}
            className="mcc-filter"
            filters={faxHooks.filterConfig.filters}
            filterLabels={faxHooks.filterConfig.filterLabels}
            selectedFilters={faxHooks.faxFilters}
            onFilterChange={faxHooks.handleFaxFilterChange}
          />
          <Button
            size="md"
            className="button-primary"
            onClick={faxHooks.onComposeFax}
            disabled={showFullEmptyState}
          >
            Send fax
          </Button>
        </div>,
      )
    return () => setTabLayerActions(null)
  }, [
    faxHooks.activeFolder,
    faxHooks.searchQuery,
    faxHooks.handleSearch,
    faxHooks.filterConfig,
    faxHooks.faxFilters,
    faxHooks.handleFaxFilterChange,
    faxHooks.onComposeFax,
    showFullEmptyState,
    setTabLayerActions,
  ])

  return (
    <>
      <div className={`mc-fax${showFullEmptyState ? ' mc-fax--empty' : ''}`}>
        {showFullEmptyState ? (
          <EmptyState
            className="mc-fax-empty"
            iconClass="mgc mgc_fax_fill"
            title="No faxes found"
            description="You don't have any faxes on record yet."
            actionLabel="Send fax"
            onAction={faxHooks.onComposeFax}
          />
        ) : (
          <>
            <div className="fax-side-panel">
              <div className="fax-side-panel-scrollable">
                {faxHooks.sidePanelSelection.map((data, index) => {
                  return (
                    <>
                      {data.map((item) => {
                        return (
                          <button
                            type="button"
                            key={`${index}-${item.key}`}
                            className={`unstyled-button folder-item${faxHooks.isSystemTabActive(item.key) ? ' active' : ''}`}
                            onClick={() => faxHooks.handleChangeTab(item.key)}
                          >
                            <span className="mcf-icon-name-wrapper">
                              <span className={`folder-icon ${item.icon}`} />
                              {item.text}
                            </span>
                            {(item.count ?? 0) > 0 ? (
                              <span
                                className={`mcf-folder-count${item.hasUnread ? ' mcf-folder-count-unread' : ''}`}
                              >
                                {item.count}
                              </span>
                            ) : null}
                          </button>
                        )
                      })}
                      <div key={index} className="horizontal-divider" />
                    </>
                  )
                })}
                <div className="collapsible-folders">
                  <div className="collapsible-folders-header">
                    <div className="right-dropdown">
                      <div className="cfh-title">
                        <span className="folder-icon mgc_folder_fill" />
                        Folders
                      </div>
                      <button
                        type="button"
                        className={`unstyled-button ${faxHooks.showFolders ? 'mgc_down_small_fill' : 'mgc_up_small_fill'}`}
                        aria-label={
                          faxHooks.showFolders
                            ? 'Collapse folders'
                            : 'Expand folders'
                        }
                        aria-expanded={faxHooks.showFolders}
                        onClick={() => faxHooks.toggleShowItems('folder')}
                      />
                    </div>
                    <button
                      type="button"
                      data-bs-toggle="tooltip"
                      title="Add folder"
                      aria-label="Add folder"
                      className="unstyled-button add-item-button mgc_add_circle_line"
                      onClick={() => faxHooks.toggleShowAddItem('folder')}
                    />
                  </div>
                  <div className="collapsible-folders-content">
                    {faxHooks.showAddFolder && (
                      <div className="add-item-container">
                        <div className="aic-row">
                          <div className="aic-input-wrapper">
                            <span className="folder-icon mgc_folder_line" />
                            <InputField
                              placeholder="Add folder"
                              value={faxHooks.newFolder.folder_name}
                              onChange={(e) =>
                                faxHooks.updateFolder(
                                  'folder_name',
                                  e.target.value,
                                )
                              }
                            />
                          </div>
                          <div className="aic-action-buttons">
                            <button
                              type="button"
                              data-bs-toggle="tooltip"
                              title="Cancel"
                              aria-label="Cancel"
                              className="unstyled-button discard-icon-button mgc_close_line"
                              onClick={faxHooks.cancelAddFolder}
                            />
                            <button
                              type="button"
                              data-bs-toggle="tooltip"
                              title="Save"
                              aria-label="Save"
                              className={`unstyled-button save-icon-button mgc_save_2_fill ${!faxHooks.newFolder.folder_name ? 'save-icon-button-disabled' : ''}`}
                              onClick={faxHooks.saveFolder}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                    {faxHooks.showFolders &&
                      faxHooks.folders.map((folder) => {
                        if (faxHooks.editingFolderId === folder.fax_folder_id) {
                          return (
                            <div
                              key={folder.fax_folder_id}
                              className="add-item-container"
                            >
                              <div className="aic-row">
                                <div className="aic-input-wrapper">
                                  <span className="folder-icon mgc_folder_line" />
                                  <InputField
                                    placeholder="Folder name"
                                    value={faxHooks.editingFolderName}
                                    onChange={(e) =>
                                      faxHooks.setEditingFolderName(
                                        e.target.value,
                                      )
                                    }
                                    handleKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        faxHooks.saveEditFolder()
                                      }
                                    }}
                                  />
                                </div>
                                <div className="aic-action-buttons">
                                  <button
                                    type="button"
                                    data-bs-toggle="tooltip"
                                    title="Cancel"
                                    aria-label="Cancel"
                                    className="unstyled-button discard-icon-button mgc_close_line"
                                    onClick={faxHooks.cancelEditFolder}
                                  />
                                  <button
                                    type="button"
                                    data-bs-toggle="tooltip"
                                    title="Save"
                                    aria-label="Save"
                                    className={`unstyled-button save-icon-button mgc_save_2_fill ${!faxHooks.editingFolderName.trim() ? 'save-icon-button-disabled' : ''}`}
                                    onClick={faxHooks.saveEditFolder}
                                  />
                                </div>
                              </div>
                            </div>
                          )
                        }
                        return (
                          <div
                            key={folder.fax_folder_id}
                            className={`cfc-items-wrapper${faxHooks.folderActive(folder.fax_folder_id ?? '') || faxHooks.hoveredItem === folder.fax_folder_id ? ' cfc-active-item' : ''}`}
                            role="button"
                            tabIndex={0}
                            aria-label={folder.folder_name}
                            onClick={() =>
                              faxHooks.handleSelectFolder(
                                folder.fax_folder_id ?? '',
                              )
                            }
                            onKeyDown={(e) => {
                              if (e.key !== 'Enter' && e.key !== ' ') return
                              if (e.target !== e.currentTarget) return
                              e.preventDefault()
                              faxHooks.handleSelectFolder(
                                folder.fax_folder_id ?? '',
                              )
                            }}
                            onMouseEnter={() =>
                              faxHooks.handleMouseEnter(
                                folder.fax_folder_id ?? '',
                              )
                            }
                            onMouseLeave={() =>
                              faxHooks.handleMouseLeave(folder.fax_folder_id)
                            }
                          >
                            <span
                              key={folder.fax_folder_id}
                              className="cfc-items"
                            >
                              <span className={`folder-icon mgc_folder_line`} />
                              {folder.folder_name}
                            </span>
                            <span className="mcf-side-item-actions">
                              {folder.count > 0 &&
                                !faxHooks.showFolderMoreOptions(
                                  folder.fax_folder_id ?? '',
                                ) && (
                                  <span
                                    className={`mcf-folder-count${folder.hasUnread ? ' mcf-folder-count-unread' : ''}`}
                                  >
                                    {folder.count}
                                  </span>
                                )}
                              {faxHooks.showFolderMoreOptions(
                                folder.fax_folder_id ?? '',
                              ) && (
                                <span
                                  className="mcf-side-more-options-wrap"
                                  onClick={(e) => e.stopPropagation()}
                                  onMouseDown={(e) => e.stopPropagation()}
                                >
                                  <DropDownButtonComponent
                                    items={faxHooks.folderMoreItems}
                                    cssClass="e-caret-hide mcf-side-more-options"
                                    iconCss="mgc_more_2_line cfc-items-more-options"
                                    open={() =>
                                      faxHooks.handleFolderDropdownOpen(
                                        folder.fax_folder_id ?? '',
                                      )
                                    }
                                    close={faxHooks.handleFolderDropdownClose}
                                    select={(args) =>
                                      faxHooks.handleFolderMoreOptionSelect(
                                        args,
                                        folder,
                                      )
                                    }
                                  />
                                </span>
                              )}
                            </span>
                          </div>
                        )
                      })}
                  </div>
                </div>
                <div className="horizontal-divider" />
                <div className="collapsible-folders">
                  <div className="collapsible-folders-header">
                    <div className="right-dropdown">
                      <div className="cfh-title">
                        <span className="folder-icon mgc_tag_2_fill" />
                        Labels
                      </div>
                      <button
                        type="button"
                        className={`unstyled-button toggle-icon ${faxHooks.showLabels ? 'mgc_down_small_fill' : 'mgc_up_small_fill'}`}
                        aria-label={
                          faxHooks.showLabels
                            ? 'Collapse labels'
                            : 'Expand labels'
                        }
                        aria-expanded={faxHooks.showLabels}
                        onClick={() => faxHooks.toggleShowItems('label')}
                      />
                    </div>
                    <button
                      type="button"
                      data-bs-toggle="tooltip"
                      title="Add label"
                      aria-label="Add label"
                      className="unstyled-button add-item-button mgc_add_circle_line"
                      onClick={() => faxHooks.toggleShowAddItem('label')}
                    />
                  </div>
                  <div className="collapsible-folders-content">
                    {faxHooks.showAddLabel && (
                      <div className="add-item-container">
                        <div className="aic-row">
                          <div className="aic-input-wrapper">
                            <span className="folder-icon mgc_tag_2_fill" />
                            <InputField
                              placeholder="Add label"
                              value={faxHooks.newLabel.tag_name}
                              onChange={(e) =>
                                faxHooks.updateLabel('tag_name', e.target.value)
                              }
                            />
                          </div>
                          <div className="aic-action-buttons">
                            <button
                              type="button"
                              data-bs-toggle="tooltip"
                              title="Cancel"
                              aria-label="Cancel"
                              className="unstyled-button discard-icon-button mgc_close_line"
                              onClick={faxHooks.cancelAddLabel}
                            />
                            <button
                              type="button"
                              data-bs-toggle="tooltip"
                              title="Save"
                              aria-label="Save"
                              className={`unstyled-button save-icon-button mgc_save_2_fill ${!faxHooks.newLabel.tag_name ? 'save-icon-button-disabled' : ''}`}
                              onClick={faxHooks.saveLabel}
                            />
                          </div>
                        </div>
                        <div className="aic-color-wrapper">
                          Select color
                          <div className="aic-color-items">
                            {Object.entries(FaxTagColor)
                              .filter(
                                (color) => color[1] !== FaxTagColor.Default,
                              )
                              .map(([key, color]) => {
                                return (
                                  <button
                                    type="button"
                                    key={color}
                                    data-bs-toggle="tooltip"
                                    title={key}
                                    aria-label={key}
                                    className={`unstyled-button aic-color-item ti ti-circle-filled ${faxHooks.newLabel.color === color ? 'active' : ''}`}
                                    style={{ color: `var(--${color})` }}
                                    onClick={() =>
                                      faxHooks.updateLabel(
                                        'color',
                                        color as FaxTagColor,
                                      )
                                    }
                                  />
                                )
                              })}
                          </div>
                        </div>
                      </div>
                    )}
                    {faxHooks.showLabels &&
                      faxHooks.labels.map((label) => {
                        if (faxHooks.editingLabelId === label.fax_tag_id) {
                          return (
                            <div
                              key={label.fax_tag_id}
                              className="add-item-container"
                            >
                              <div className="aic-row">
                                <div className="aic-input-wrapper">
                                  <span className="folder-icon mgc_tag_2_fill" />
                                  <InputField
                                    placeholder="Label name"
                                    value={
                                      faxHooks.editingLabel?.tag_name ?? ''
                                    }
                                    onChange={(e) =>
                                      faxHooks.updateEditLabel(
                                        'tag_name',
                                        e.target.value,
                                      )
                                    }
                                  />
                                </div>
                                <div className="aic-action-buttons">
                                  <button
                                    type="button"
                                    data-bs-toggle="tooltip"
                                    title="Cancel"
                                    aria-label="Cancel"
                                    className="unstyled-button discard-icon-button mgc_close_line"
                                    onClick={faxHooks.cancelEditLabel}
                                  />
                                  <button
                                    type="button"
                                    data-bs-toggle="tooltip"
                                    title="Save"
                                    aria-label="Save"
                                    className={`unstyled-button save-icon-button mgc_save_2_fill ${!faxHooks.editingLabel?.tag_name?.trim() ? 'save-icon-button-disabled' : ''}`}
                                    onClick={faxHooks.saveEditLabel}
                                  />
                                </div>
                              </div>
                              <div className="aic-color-wrapper">
                                Select color
                                <div className="aic-color-items">
                                  {Object.entries(FaxTagColor)
                                    .filter(
                                      (color) =>
                                        color[1] !== FaxTagColor.Default,
                                    )
                                    .map(([key, color]) => {
                                      return (
                                        <button
                                          type="button"
                                          key={color}
                                          data-bs-toggle="tooltip"
                                          title={key}
                                          aria-label={key}
                                          className={`unstyled-button aic-color-item ti ti-circle-filled ${faxHooks.editingLabel?.color === color ? 'active' : ''}`}
                                          style={{ color: `var(--${color})` }}
                                          onClick={() =>
                                            faxHooks.updateEditLabel(
                                              'color',
                                              color as FaxTagColor,
                                            )
                                          }
                                        />
                                      )
                                    })}
                                </div>
                              </div>
                            </div>
                          )
                        }
                        return (
                          <div
                            key={label.fax_tag_id}
                            className={`cfc-items-wrapper${faxHooks.tagActive(label.fax_tag_id ?? '') || faxHooks.hoveredItem === label.fax_tag_id ? ' cfc-active-item' : ''}`}
                            role="button"
                            tabIndex={0}
                            aria-label={label.tag_name}
                            onClick={() =>
                              faxHooks.handleSelectTag(label.fax_tag_id ?? '')
                            }
                            onKeyDown={(e) => {
                              if (e.key !== 'Enter' && e.key !== ' ') return
                              if (e.target !== e.currentTarget) return
                              e.preventDefault()
                              faxHooks.handleSelectTag(label.fax_tag_id ?? '')
                            }}
                            onMouseEnter={() =>
                              faxHooks.handleMouseEnter(label.fax_tag_id ?? '')
                            }
                            onMouseLeave={() =>
                              faxHooks.handleMouseLeave(label.fax_tag_id)
                            }
                          >
                            <span className="cfc-items">
                              <span
                                className={`tag-icon mgc_tag_2_fill`}
                                style={
                                  {
                                    '--tag-color': `var(--${label.color})`,
                                  } as React.CSSProperties
                                }
                              />
                              {label.tag_name}
                            </span>
                            <span className="mcf-side-item-actions">
                              {label.count > 0 &&
                                !faxHooks.showLabelMoreOptions(
                                  label.fax_tag_id ?? '',
                                ) && (
                                  <span
                                    className={`mcf-folder-count${label.hasUnread ? ' mcf-folder-count-unread' : ''}`}
                                  >
                                    {label.count}
                                  </span>
                                )}
                              {faxHooks.showLabelMoreOptions(
                                label.fax_tag_id ?? '',
                              ) && (
                                <span
                                  className="mcf-side-more-options-wrap"
                                  onClick={(e) => e.stopPropagation()}
                                  onMouseDown={(e) => e.stopPropagation()}
                                >
                                  <DropDownButtonComponent
                                    items={faxHooks.labelMoreItems}
                                    cssClass="e-caret-hide mcf-side-more-options"
                                    iconCss="mgc_more_2_line cfc-items-more-options"
                                    open={() =>
                                      faxHooks.handleLabelDropdownOpen(
                                        label.fax_tag_id ?? '',
                                      )
                                    }
                                    close={faxHooks.handleLabelDropdownClose}
                                    select={(args) =>
                                      faxHooks.handleLabelMoreOptionSelect(
                                        args,
                                        label,
                                      )
                                    }
                                  />
                                </span>
                              )}
                            </span>
                          </div>
                        )
                      })}
                  </div>
                </div>
              </div>
            </div>
            <div className="fax-prev-col">
              {faxHooks.activeTab === 'compose' ? (
                <FaxCompose />
              ) : (
                <FaxPreview />
              )}
            </div>
          </>
        )}
      </div>
      <MessageCenterDeleteFaxOrganizeModal
        show={Boolean(faxHooks.pendingDelete)}
        itemType={faxHooks.pendingDelete?.type ?? 'folder'}
        itemName={faxHooks.pendingDelete?.name ?? ''}
        onCancel={faxHooks.hidePendingDelete}
        onDelete={faxHooks.confirmPendingDelete}
      />
    </>
  )
}

export default MessageCenterFax
